/* ===========================================================================
   Small numeric helpers.
   =========================================================================== */

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/** Hamming weight of a 32-bit word (SWAR). */
export function popcount32(v) {
  v = v - ((v >>> 1) & 0x55555555);
  v = (v & 0x33333333) + ((v >>> 2) & 0x33333333);
  v = (v + (v >>> 4)) & 0x0f0f0f0f;
  return (v * 0x01010101) >>> 24;
}

/**
 * In-place selection: `buffer[k]` ends up holding the k-th smallest value, and
 * everything before it is no larger. C++'s nth_element, and the same partition
 * `smallestRows` runs over row indices.
 */
export function nthElement(buffer, k) {
  let lo = 0,
    hi = buffer.length - 1;
  while (lo < hi) {
    const pivot = buffer[(lo + hi) >> 1];
    let i = lo,
      j = hi;
    while (i <= j) {
      while (buffer[i] < pivot) i++;
      while (buffer[j] > pivot) j--;
      if (i <= j) {
        const swap = buffer[i];
        buffer[i] = buffer[j];
        buffer[j] = swap;
        i++;
        j--;
      }
    }
    if (k <= j) hi = j;
    else if (k >= i) lo = i;
    else break; // k landed inside the pivot's own run
  }
  return buffer[k];
}

/**
 * Median of a Float32Array/Array, matching numpy's mean-of-middle-two for even lengths.
 *
 * Selected, not sorted. The adaptive Canny threshold takes the median of the whole
 * blurred frame on every detect, and ordering 291,000 floats to read the middle one was
 * 25 ms of a 165 ms detection. The small callers — edge profiles, line residuals — go
 * through the same path and are indifferent either way.
 */
export function median(values) {
  const n = values.length;
  if (n === 0) return 0;
  const buffer = Float64Array.from(values);
  const mid = n >> 1;
  const upper = nthElement(buffer, mid);
  if (n % 2) return upper;
  // The lower of the two middles is the largest value left of the partition, which the
  // selection has already gathered there.
  let lower = -Infinity;
  for (let i = 0; i < mid; i++) if (buffer[i] > lower) lower = buffer[i];
  return 0.5 * (lower + upper);
}

/** Hamming distance between two 64-bit hashes held as [lo, hi] uint32 pairs. */
export function hammingPair(a, b) {
  return popcount32((a[0] ^ b[0]) >>> 0) + popcount32((a[1] ^ b[1]) >>> 0);
}
